import React, { useMemo, useState } from "react";
import { t } from "../i18n";
import "./Landing.css";

function Field({ label, type = "text", value, onChange, placeholder, autoComplete }) {
  return (
    <label className="field">
      <div className="label">{label}</div>
      <input
        className="input"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
      />
    </label>
  );
}

export default function Landing() {
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetStatus, setResetStatus] = useState("idle");
  const [resetMsg, setResetMsg] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  const canSubmit = useMemo(() => {
    if (!email) return false;
    if (tab === "signup") return Boolean(name && password);
    return Boolean(password);
  }, [email, password, name, tab]);

  async function handlePasswordAuth(e) {
    e.preventDefault();
    setMsg(null);
    setLoading(true);
    try {
      const url = tab === "login" ? "/api/auth/login" : "/api/auth/signup";
      const body = tab === "login" ? { email, password } : { name, email, password };

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Erreur inconnue");

      if (tab === "signup" && data.needsEmailConfirmation) {
        setMsg({ type: "info", text: "Compte cree. Verifie ton email pour activer." });
        return;
      }

      window.location.href = "/nano";
    } catch (err) {
      setMsg({ type: "error", text: err.message || "Erreur" });
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    setResetStatus("loading");
    setResetMsg("");
    try {
      const res = await fetch("/api/auth/forgot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: resetEmail,
          redirectTo: "https://aczart.fr/reset-password"
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Erreur");
      setResetStatus("success");
      setResetMsg(t("landing.forgot.sent"));
    } catch (err) {
      setResetStatus("error");
      setResetMsg(err.message || "Erreur");
    }
  }

  return (
    <div className="page">
      <header className="topbar">
        <div className="brand">
          <div className="logo" />
          <div>
            <div className="brandName">Contentos</div>
            <div className="brandTag">{t("landing.brand.tag")}</div>
          </div>
        </div>
        <nav className="nav">
          <a href="#features">{t("landing.nav.features")}</a>
          <a href="#workflow">{t("landing.nav.workflow")}</a>
          <a href="#faq">{t("landing.nav.faq")}</a>
        </nav>
      </header>

      <main className="hero">
        <section className="heroLeft">
          <h1>{t("landing.hero.title")}</h1>
          <p className="lead">
            {t("landing.hero.lead")}
          </p>

          <div className="badges" id="features">
            <div className="badge">{t("landing.badge.templates")}</div>
            <div className="badge">{t("landing.badge.subtitles")}</div>
            <div className="badge">{t("landing.badge.exports")}</div>
            <div className="badge">{t("landing.badge.editing")}</div>
          </div>

          <div className="panel" id="workflow">
            <h3>{t("landing.workflow.title")}</h3>
            <ol>
              <li>{t("landing.workflow.step1")}</li>
              <li>{t("landing.workflow.step2")}</li>
              <li>{t("landing.workflow.step3")}</li>
            </ol>
          </div>
        </section>

        <section className="heroRight">
          <div className="card">
            <div className="tabs">
              <button className={tab === "login" ? "tab active" : "tab"} onClick={() => setTab("login")}>{t("landing.tabs.login")}</button>
              <button className={tab === "signup" ? "tab active" : "tab"} onClick={() => setTab("signup")}>{t("landing.tabs.signup")}</button>
            </div>

            <form onSubmit={handlePasswordAuth} className="form">
              {tab === "signup" && (
                <Field
                  label={t("landing.field.name")}
                  value={name}
                  onChange={setName}
                  placeholder={t("landing.placeholder.name")}
                  autoComplete="name"
                />
              )}

              <Field
                label={t("landing.field.email")}
                type="email"
                value={email}
                onChange={setEmail}
                placeholder={t("landing.placeholder.email")}
                autoComplete="email"
              />

              <Field
                label={t("landing.field.password")}
                type="password"
                value={password}
                onChange={setPassword}
                placeholder={t("landing.placeholder.password")}
                autoComplete={tab === "login" ? "current-password" : "new-password"}
              />

              {tab === "login" && (
                <button
                  type="button"
                  className="forgot-link"
                  onClick={() => {
                    setResetEmail(email);
                    setResetStatus("idle");
                    setResetMsg("");
                    setResetOpen(true);
                  }}
                >
                  {t("landing.forgot.link")}
                </button>
              )}

              {msg?.type === "error" && <div className="alert error">{msg.text}</div>}
              {msg?.type === "info" && <div className="alert info">{msg.text}</div>}

              <button className="btn" disabled={!canSubmit || loading}>
                {loading ? "…" : tab === "login" ? t("landing.submit.login") : t("landing.submit.signup")}
              </button>

              <div className="fineprint">
                {t("terms.prefix")}{" "}
                <a href="/legal#cgu">{t("terms.cgu")}</a>{" "}
                {t("terms.and")}{" "}
                <a href="/legal#privacy">{t("terms.privacy")}</a>.
              </div>
            </form>
          </div>

          <div className="mini" id="faq">
            <h4>{t("landing.faq.title")}</h4>
            <details>
              <summary>{t("landing.faq.access")}</summary>
              <p>{t("landing.faq.access.answer")} <code>/nano</code>.</p>
            </details>
            <details>
              <summary>{t("landing.faq.design")}</summary>
              <p>{t("landing.faq.design.answer")}</p>
            </details>
          </div>
        </section>
      </main>

      {resetOpen && (
        <div className="auth-modal-overlay" role="dialog" aria-modal="true">
          <div className="auth-modal">
            <div className="auth-modal-header">
              <h3>{t("landing.forgot.title")}</h3>
              <button
                type="button"
                className="auth-modal-close"
                onClick={() => setResetOpen(false)}
              >
                ×
              </button>
            </div>
            <p className="auth-modal-subtitle">{t("landing.forgot.subtitle")}</p>
            <form onSubmit={handleResetPassword} className="auth-modal-form">
              <Field
                label={t("landing.forgot.email")}
                type="email"
                value={resetEmail}
                onChange={setResetEmail}
                placeholder={t("landing.placeholder.email")}
                autoComplete="email"
              />
              {resetMsg && (
                <div className={`alert ${resetStatus === "success" ? "info" : "error"}`}>{resetMsg}</div>
              )}
              <button className="btn" disabled={!resetEmail || resetStatus === "loading"}>
                {resetStatus === "loading" ? "…" : t("landing.forgot.submit")}
              </button>
            </form>
          </div>
        </div>
      )}

      <footer className="footer">
        <span>© {new Date().getFullYear()} Contentos</span>
        <span className="footer-links">
          <a href="/legal#cgu">{t("terms.cgu")}</a>
          <a href="/legal#privacy">{t("terms.privacy")}</a>
        </span>
      </footer>
    </div>
  );
}
